import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { api } from '@/api/client';

interface Billing {
  isApproved?: boolean;
  /** True when the server had no saved billing row and returned Profile / pickup-location
   *  defaults instead. Nothing is stored until this form is saved. */
  isPrefill?: boolean;
  companyName?: string;
  gstNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  stateId?: number | '';
  pincode?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
}

interface State { id: number; name: string }

export function MerchantBillingTab({ merchantId, merchantView = false }: { merchantId: number; merchantView?: boolean }) {
  const qc = useQueryClient();

  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get<State[]>('/states')).data,
    staleTime: 1000 * 60 * 60,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['merchant-billing', merchantId],
    queryFn: async () => {
      try { return (await api.get<Billing>(`/merchants/${merchantId}/billing`)).data; }
      catch { return {} as Billing; }
    },
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Billing>({ values: data ?? {} });
  // A merchant fills billing once; after it's submitted (company + address saved) or admin-approved it
  // becomes view-only for them — contact support to change. Admins (merchantView=false) can still edit.
  //
  // `isPrefill` must break the lock: those values are suggestions the server assembled from the
  // Profile tab and the pickup location, not something this merchant has submitted. Treating them as
  // submitted would show a merchant a filled, locked form for a record that does not exist, and
  // they could never save it.
  const hasBilling = !data?.isPrefill && !!(data?.companyName && data?.address);
  const readOnly = merchantView && !data?.isPrefill && (!!data?.isApproved || hasBilling);

  const save = useMutation({
    mutationFn: async (d: Billing) => {
      const payload: any = { ...d };
      payload.stateId = Number(payload.stateId);
      // Mirror stateId into state (name) for display systems. The server re-resolves the name from
      // the id anyway, so the two can't drift.
      const s = states?.find(x => x.id === payload.stateId);
      if (s) payload.state = s.name;
      return (await api.put(`/merchants/${merchantId}/billing`, payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-billing', merchantId] }); toast.success('Billing saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save billing'),
  });

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5">
      {data?.isPrefill && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <b>Not saved yet.</b> These details were copied from the Profile tab and this merchant&apos;s
          pickup location. Check them, add anything missing — <b>State is required</b>, it decides
          CGST/SGST vs IGST on the invoice — then press Save.
        </div>
      )}
      {readOnly && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
          ✓ {data?.isApproved
            ? 'Approved by admin — contact support to make changes.'
            : 'Your billing details are submitted and locked. Contact support to make changes.'}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Billing Company Name *" error={errors.companyName?.message}>
          <input {...register('companyName', { required: 'Required' })} disabled={readOnly} className="input disabled:bg-slate-50" />
        </Field>
        <Field label="GST Number">
          <input {...register('gstNumber')} disabled={readOnly} placeholder="22AAAAA0000A1Z5" className="input uppercase disabled:bg-slate-50" />
        </Field>
      </div>

      <Field label="Billing Address *" error={errors.address?.message}>
        <textarea {...register('address', { required: 'Required' })} disabled={readOnly} rows={2} className="input disabled:bg-slate-50" />
      </Field>

      <div className="grid grid-cols-3 gap-4">
        <Field label="City *" error={errors.city?.message}>
          <input {...register('city', { required: 'Required' })} disabled={readOnly} className="input disabled:bg-slate-50" />
        </Field>
        {/* State is the place of supply — it decides CGST/SGST vs IGST on the invoice — so it is
            required here and on the server, not optional as it used to be. */}
        <Field label="State *" error={(errors.stateId as any)?.message}>
          <select {...register('stateId', { required: 'Required' })} disabled={readOnly} className="input disabled:bg-slate-50">
            <option value="">— Select state —</option>
            {states?.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Pincode *" error={errors.pincode?.message}>
          <input {...register('pincode', { required: 'Required', pattern: { value: /^\d{6}$/, message: '6 digits' } })} disabled={readOnly} className="input font-mono disabled:bg-slate-50" />
        </Field>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="text-sm font-medium text-slate-700 mb-2">Billing Contact</div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Person">
            <input {...register('contactPerson')} disabled={readOnly} className="input disabled:bg-slate-50" />
          </Field>
          <Field label="Phone">
            <input {...register('contactPhone')} disabled={readOnly} className="input disabled:bg-slate-50" />
          </Field>
          <Field label="Email">
            <input type="email" {...register('contactEmail')} disabled={readOnly} className="input disabled:bg-slate-50" />
          </Field>
        </div>
      </div>

      {!readOnly && <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="button" onClick={() => reset(data ?? {})} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Reset</button>
        <button type="submit" disabled={save.isPending}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Billing
        </button>
      </div>}
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}

